import React, { createContext, useEffect, useState } from "react";
import { Toast } from "../components/common/Toast";
import { ACCESS_TOKEN, PROFILE } from "../helpers/Constant";
import { getErrorMessage } from "../helpers/Utils";
import AuthService from "../services/AuthService";
import RoleService from "../services/RoleService";
import UserService from "../services/UserService";

export const AuthContext = createContext("AuthContext");

const auth = localStorage.getItem(ACCESS_TOKEN);
const user = localStorage.getItem(PROFILE);

const AuthContextProvider = ({ children }) => {
    const [permissions, setPermissions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [profileLoading, setProfileLoading] = useState(false);
	const [isLogin, setIsLogin] = useState(!!auth);
	const [profile, setProfile] = useState(user ? JSON.parse(user) : null);
	const [role, setRole] = useState(
		user && JSON.parse(user).role ? JSON.parse(user).role.alias : null,
	);

	useEffect(() => {
		if (auth) {
			getUserProfile();
		}
	}, []);

	const loadPermissions = async () => {
		try {
			const response = await RoleService.getUserPermissions();
			const allPerms = response.data.flatMap(
				(module) => module.permissions,
			);

			setPermissions(allPerms.map((p) => p.permissionName));
		} catch (error) {
			setPermissions([]);
		}
	};

	const login = async (credential) => {
		setLoading(true);
		try {
			const response = await AuthService.login(credential);
			localStorage.setItem(ACCESS_TOKEN, response.data.token);
			localStorage.setItem(PROFILE, JSON.stringify(response.data.user));

			setIsLogin(true);
			setProfile(response.data.user);
			setRole(response.data.user.role.alias);

			await loadPermissions();
		} catch (error) {
			setIsLogin(false);
			Toast("error", "Error", getErrorMessage(error));
		} finally {
			setLoading(false);
		}
	};

	const refreshToken = async () => {
		setLoading(true);
		try {
			const response = await AuthService.refreshToken();
			localStorage.setItem(ACCESS_TOKEN, response.data.token);
			localStorage.setItem(PROFILE, JSON.stringify(response.data.user));

			setIsLogin(true);
			setProfile(response.data.user);
			setRole(response.data.user.role.alias);

			await loadPermissions();
		} catch (error) {
			setIsLogin(false);
			Toast("error", "Error", getErrorMessage(error));
		} finally {
			setLoading(false);
		}
	};

	const getUserProfile = async () => {
		setProfileLoading(true);
		try {
			const response = await UserService.getUserProfile();
			if (response.data.needToRefreshToken) {
				await refreshToken();
				return;
			}
			localStorage.setItem(PROFILE, JSON.stringify(response.data));

			setIsLogin(true);
			setProfile(response.data);
			setRole(response.data.role.alias);

			await loadPermissions();
		} catch (error) {
			const status = error.response?.status;
			if (status === 401 || status === 404) {
				logout();
			} else {
				Toast("error", "Error", getErrorMessage(error));
			}
		} finally {
			setProfileLoading(false);
		}
	};

	const logout = () => {
		setIsLogin(false);
		localStorage.clear();
	};

	return (
		<AuthContext.Provider
			value={{
				isLogin,
				profile,
				loading,
				profileLoading,
				role,
				permissions: permissions,
				login,
				logout,
				getUserProfile,
			}}
		>
			{children}
		</AuthContext.Provider>
	);
};

export default AuthContextProvider;
